import React, { useState, useEffect } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSpecialty } from '../contexts/SpecialtyContext';
import { Home, FileText, User, Users, Settings, LogOut, Menu } from 'lucide-react';
import cuoreAnatomico from '../assets/img/cuore-anatomico.png';

interface LayoutProps {
  children: React.ReactNode;
  requiresSpecialty?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, requiresSpecialty = true }) => {
  const { logout, profile } = useAuth();
  const { selectedSpecialty } = useSpecialty();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);

  // Reindirizza alla selezione specialistica se necessario
  useEffect(() => {
    if (requiresSpecialty && !selectedSpecialty) {
      navigate('/specialty-selection');
    }
  }, [selectedSpecialty, requiresSpecialty, navigate]);

  // Se richiede specialistica ma non è selezionata, non mostrare nulla
  if (requiresSpecialty && !selectedSpecialty) {
    return <div className="text-center p-4">Reindirizzamento...</div>;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home size={20} /> },
    { path: `/fatture/${selectedSpecialty}`, label: 'Fatture', icon: <FileText size={20} /> },
    { path: `/referti/${selectedSpecialty}`, label: 'Referti', icon: <User size={20} /> },
    { path: '/clienti', label: 'Clienti', icon: <Users size={20} /> },
    { path: '/settings', label: 'Impostazioni', icon: <Settings size={20} /> },
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`.toUpperCase();

  const Brand = () => (
    <div className="app-brand">
      <span className="app-brand-mark">
        <img src={cuoreAnatomico} alt="" />
      </span>
      <span className="app-brand-name">Cardio<span>Ref</span></span>
    </div>
  );

  const Sidebar = () => (
    <div className="app-sidebar-inner">
      <Brand />
      <nav className="app-nav">
        {menuItems.map((item) => (
          <button
            key={item.path}
            className={`app-nav-link ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => {
              navigate(item.path);
              setShowSidebar(false);
            }}
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="app-user">
        <div className="app-user-avatar">{initials || <User size={18} />}</div>
        <div className="app-user-info">
          <small>Benvenuto</small>
          <strong>{profile?.first_name} {profile?.last_name}</strong>
        </div>
        <button className="app-user-logout" onClick={handleLogout} title="Logout" aria-label="Logout">
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-shell">
      {/* Filtro duotone: il cuore anatomico nei toni vino/rosa */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <filter id="heart-duotone" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.33 0.59 0.11 0 0  0.33 0.59 0.11 0 0  0.33 0.59 0.11 0 0  0 0 0 1 0"
          />
          <feComponentTransfer>
            <feFuncR type="table" tableValues="0.45 1" />
            <feFuncG type="table" tableValues="0.04 0.78" />
            <feFuncB type="table" tableValues="0.14 0.82" />
          </feComponentTransfer>
        </filter>
        {/* Variante per fondi scuri: linee rosa, riempimento bordeaux */}
        <filter id="heart-duotone-light" colorInterpolationFilters="sRGB">
          <feColorMatrix
            type="matrix"
            values="0.33 0.59 0.11 0 0  0.33 0.59 0.11 0 0  0.33 0.59 0.11 0 0  0 0 0 1 0"
          />
          <feComponentTransfer>
            <feFuncR type="table" tableValues="1 0.6" />
            <feFuncG type="table" tableValues="0.55 0.14" />
            <feFuncB type="table" tableValues="0.66 0.26" />
          </feComponentTransfer>
        </filter>
      </svg>

      {/* Sidebar desktop */}
      <aside className="app-sidebar d-none d-md-block">
        <Sidebar />
      </aside>

      {/* Sidebar mobile */}
      <Offcanvas show={showSidebar} onHide={() => setShowSidebar(false)} placement="start" className="app-offcanvas">
        <Offcanvas.Header closeButton />
        <Offcanvas.Body className="p-0">
          <Sidebar />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main content */}
      <div className="app-content">
        <header className="app-topbar d-md-none">
          <Button
            variant="outline-secondary"
            className="app-topbar-menu"
            onClick={() => setShowSidebar(true)}
            aria-label="Apri menu"
          >
            <Menu />
          </Button>
          <Brand />
        </header>

        <main className="app-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
